/**
 * OCR Processor Utility
 * 
 * Uses Tesseract.js for client-side OCR processing
 */

import { createWorker } from 'tesseract.js';

class OCRProcessor {
  constructor() {
    this.isInitialized = false;
    this.worker = null;
    this.supportedLanguages = ['eng', 'spa', 'fra', 'deu', 'ita'];
  }

  /**
   * Initialize OCR processor with Tesseract.js
   */
  async initialize(language = 'eng') {
    if (this.isInitialized && this.worker) {
      return true;
    }

    try {
      console.log('Initializing Tesseract.js OCR processor...');
      this.worker = await createWorker(language);
      this.isInitialized = true;
      console.log('OCR Processor initialized successfully');
      return true;
    } catch (error) {
      console.error('Failed to initialize OCR processor:', error);
      throw new Error(`OCR initialization failed: ${error.message}`);
    }
  }

  /**
   * Terminate the worker to free resources
   */
  async terminate() {
    if (this.worker) {
      await this.worker.terminate();
      this.worker = null;
      this.isInitialized = false;
    }
  }

  /**
   * Process image file with OCR
   * @param {File} imageFile - Image file to process
   * @param {Object} options - Processing options
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<string>} - Extracted text
   */
  async processImage(imageFile, options = {}, onProgress = null) {
    const { language = 'eng' } = options;

    // Validate file
    this.validateFile(imageFile);

    // Initialize or switch language if needed
    if (!this.isInitialized || !this.worker) {
      await this.initialize(language);
    } else {
      // Switch language if different - recreate worker with new language
      try {
        await this.terminate();
        await this.initialize(language);
      } catch (error) {
        console.warn(`Failed to switch language to ${language}, using current language`);
      }
    }

    console.log(`Processing image: ${imageFile.name}, Language: ${language}`);

    try {
      // Perform OCR with progress updates
      const { data: { text } } = await this.worker.recognize(imageFile, {
        logger: onProgress ? (m) => {
          if (m.status === 'recognizing text') {
            onProgress({
              status: 'recognizing',
              progress: m.progress || 0,
              message: `Recognizing text... ${Math.round((m.progress || 0) * 100)}%`
            });
          }
        } : undefined
      });

      return text.trim();
    } catch (error) {
      console.error('OCR processing error:', error);
      throw new Error(`Failed to process image: ${error.message}`);
    }
  }

  /**
   * Process multiple images
   * @param {File[]} imageFiles - Array of image files
   * @param {Object} options - Processing options
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<Array<{file: File, text: string}>>} - Array of results
   */
  async processMultipleImages(imageFiles, options = {}, onProgress = null) {
    const results = [];
    const total = imageFiles.length;
    
    for (let i = 0; i < imageFiles.length; i++) {
      const file = imageFiles[i];
      
      // Progress callback for individual image
      const imageProgress = onProgress ? (progress) => {
        const overallProgress = (i / total) + (progress.progress || 0) / total;
        onProgress({
          current: i + 1,
          total: total,
          percent: Math.round(overallProgress * 100),
          file: file.name,
          status: progress.status || 'processing',
          message: `Processing ${file.name}... (${i + 1}/${total})`
        });
      } : null;
      
      try {
        const text = await this.processImage(file, options, imageProgress);
        results.push({ file, text, success: true });
      } catch (error) {
        console.error(`Failed to process ${file.name}:`, error);
        results.push({ file, text: '', success: false, error: error.message });
      }
    }
    
    return results;
  }

  /**
   * Parse OCR text into structured data
   * @param {string} ocrText - Raw OCR text
   * @param {string} type - 'answerKey' or 'studentWork'
   * @returns {Object} - Structured data
   */
  parseOCRText(ocrText, type = 'answerKey') {
    try {
      if (type === 'answerKey') {
        return this.parseAnswerKeyFromOCR(ocrText);
      } else {
        return this.parseStudentWorkFromOCR(ocrText);
      }
    } catch (error) {
      console.error('Error parsing OCR text:', error);
      throw new Error(`Failed to parse OCR text: ${error.message}`);
    }
  }

  /**
   * Parse answer key from OCR text
   */
  parseAnswerKeyFromOCR(text) {
    // This is a simplified parser - in production, you'd use more sophisticated NLP
    const lines = text.split('\n').filter(line => line.trim());
    const questions = [];
    let currentQuestion = null;

    for (const line of lines) {
      // Look for question patterns
      const questionMatch = line.match(/^(\d+)[\.\)]\s*(.+)/i) || 
                           line.match(/^Question\s*(\d+):?\s*(.+)/i);
      
      if (questionMatch) {
        // Save previous question if exists
        if (currentQuestion) {
          questions.push(currentQuestion);
        }
        
        currentQuestion = {
          questionNumber: questionMatch[1],
          question: questionMatch[2],
          correctAnswer: '',
          maxScore: 10,
          rubric: { accuracy: 40, completeness: 30, clarity: 20, originality: 10 }
        };
      } 
      // Look for answer patterns
      else if (currentQuestion && line.match(/answer:/i)) {
        currentQuestion.correctAnswer = line.replace(/answer:\s*/i, '');
      }
      // Look for score patterns
      else if (currentQuestion && line.match(/score|points/i)) {
        const scoreMatch = line.match(/(\d+)/);
        if (scoreMatch) {
          currentQuestion.maxScore = parseInt(scoreMatch[1]);
        }
      }
      // Accumulate answer text
      else if (currentQuestion && currentQuestion.correctAnswer === '') {
        currentQuestion.question += ' ' + line;
      } else if (currentQuestion && currentQuestion.correctAnswer) {
        currentQuestion.correctAnswer += ' ' + line;
      }
    }

    // Add last question
    if (currentQuestion) {
      questions.push(currentQuestion);
    }

    return { questions };
  }

  /**
   * Parse student work from OCR text
   */
  parseStudentWorkFromOCR(text) {
    const lines = text.split('\n').filter(line => line.trim());
    const answers = [];
    let currentAnswer = null;
    let studentName = 'Anonymous';

    // Try to extract student name
    const nameMatch = text.match(/name:\s*([^\n]+)/i) || 
                     text.match(/student:\s*([^\n]+)/i);
    if (nameMatch) {
      studentName = nameMatch[1].trim();
    }

    for (const line of lines) {
      // Look for answer patterns
      const answerMatch = line.match(/^(\d+)[\.\)]\s*(.+)/i) || 
                         line.match(/^Question\s*(\d+).*answer:/i);
      
      if (answerMatch) {
        // Save previous answer if exists
        if (currentAnswer) {
          answers.push(currentAnswer);
        }
        
        currentAnswer = {
          questionNumber: answerMatch[1],
          answer: answerMatch[2] || ''
        };
      } 
      // Accumulate answer text
      else if (currentAnswer) {
        currentAnswer.answer += ' ' + line;
      }
    }

    // Add last answer
    if (currentAnswer) {
      answers.push(currentAnswer);
    }

    return {
      studentName,
      answers
    };
  }


  /**
   * Validate if file is suitable for OCR
   * @param {File} file - File to validate
   * @returns {boolean} - Whether file is suitable
   */
  validateFile(file) {
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/tiff', 'application/pdf'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!validTypes.includes(file.type)) {
      throw new Error(`Unsupported file type: ${file.type}. Please upload an image or PDF.`);
    }

    if (file.size > maxSize) {
      throw new Error(`File too large: ${(file.size / 1024 / 1024).toFixed(2)}MB. Maximum size is 10MB.`);
    }

    return true;
  }

  /**
   * Get supported languages
   * @returns {Array} - Array of supported language codes
   */
  getSupportedLanguages() {
    return this.supportedLanguages;
  }

  /**
   * Get language name from code
   * @param {string} code - Language code
   * @returns {string} - Language name
   */
  getLanguageName(code) {
    const languages = {
      'eng': 'English',
      'spa': 'Spanish',
      'fra': 'French',
      'deu': 'German',
      'ita': 'Italian'
    };
    return languages[code] || code;
  }
}

// Create singleton instance
const ocrProcessor = new OCRProcessor();

export default ocrProcessor;
